import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAnalytics } from "@/hooks/useAnalytics";
import { hashEmail } from "@/lib/analytics";
import { toApiErrorCode } from "@/utils/analyticsErrorCode";
import { Button } from "@musa.tech/design-system";
import { keycloakApi } from "@/integrations/keycloak/client";
import { supabase } from "@/integrations/supabase/client";
import { UserForm } from "@/components/Users/UserForm";
import useUnifiedAuth from "@/hooks/useUnifiedAuth";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { AccessDenied } from "@/components/AccessDenied";
import { useUnidades } from "@/hooks/useUnidades";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import type { UserWithPermissions } from "@/types/user";
import type { KeycloakUser } from "@/integrations/keycloak/client";
import {
  ADMIN_PROFILE_KEY,
  diffProfiles,
  withMandatoryProfiles,
  getPermissionProfileLabel,
} from "@/constants/permissionProfiles";
import type { ProfileOperationFailure } from "@/integrations/keycloak/client";

type SubmitData = {
  firstName?: string;
  lastName?: string;
  user_name?: string;
  email?: string;
  phone_number?: string;
  permission_role?: string;
  temp_password?: string;
  current_password?: string;
  new_password?: string;
  generator_links?: Array<{
    generator_id: number;
    role: string;
    include_children: boolean;
    establishment_ids?: number[];
  }>;
  profiles?: string[];
};

/**
 * O usuário foi gravado, mas o diff de perfis não foi aplicado (GR-637).
 *
 * Não é sucesso nem é falha: na criação o usuário já existe no banco, e dizer
 * "erro ao criar" mandaria o admin tentar de novo e bater em e-mail duplicado.
 * A tela segura esse estado e oferece reaplicar só os perfis.
 */
type PendingProfiles = {
  userId: number;
  email: string;
  desired: string[];
  /** Perfis que já estavam aplicados — base do diff no retry. */
  baseline: string[];
  reason: string;
  failed?: ProfileOperationFailure[];
  /** `true` quando o usuário acabou de ser criado nesta tela. */
  fromCreation: boolean;
  /** MC-91: `true` quando a criação, na verdade, reativou um excluído/incompleto. */
  fromReactivation?: boolean;
  /**
   * `true` quando o PATCH (que já carrega o `role` derivado do perfil
   * `admin`) foi confirmado ANTES do PUT de perfis falhar. Achado em review:
   * nessa ordem, o nível de acesso (Admin/Operacional) já mudou de verdade
   * quando este aviso aparece — o texto genérico "perfis não aplicados"
   * escondia isso.
   */
  roleAlreadyChanged?: boolean;
};

interface GeneratorOption {
  id: number;
  name: string;
}

interface EstablishmentOption {
  id: number;
  name: string;
  parent_id: number | null;
  n_level: number | null;
}

const GestaoUsuario = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile, keycloakToken, isLoading: isAuthLoading } = useUnifiedAuth();
  const { canManageUsers } = usePermissions();
  const { data: unidades } = useUnidades();
  const rawGenerators = unidades?.generators ?? [];
  const rawEstablishments = unidades?.establishments ?? [];

  const { trackButtonClick, trackModalOpen, trackError } = useAnalytics();

  const userId = searchParams.get("id");
  const isEditMode = searchParams.get("mode") === "edit";
  const openPasswordSection = searchParams.get("senha") === "true";
  const isPasswordRecovery = searchParams.get("recovery") === "true";
  const isEditingSelf = isEditMode && profile?.user_id === userId;

  // Entrada no funil pelo caminho da página dedicada. Só criação — edição está
  // fora de escopo nesta versão (GR-712, seção 9).
  useEffect(() => {
    if (!isEditMode) {
      trackModalOpen({ modalName: "Adicionar Usuário" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // ---------------------------------------------------------------------------
  // Options
  // ---------------------------------------------------------------------------
  const generatorOptions = useMemo((): GeneratorOption[] => {
    return (rawGenerators ?? [])
      .map((g) => ({ id: parseInt(g.id, 10), name: g.name || "" }))
      .filter((g) => !Number.isNaN(g.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawGenerators]);

  const establishmentOptions = useMemo((): EstablishmentOption[] => {
    return (rawEstablishments ?? [])
      .map((e) => ({
        id: parseInt(e.id, 10),
        name: e.name || "",
        parent_id: e.parent_id,
        n_level: e.n_level != null ? Number(e.n_level) : null,
        generator_id: e.generator_id != null ? parseInt(e.generator_id, 10) : null,
      }))
      .filter((e) => !Number.isNaN(e.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawEstablishments]);

  // ---------------------------------------------------------------------------
  // Fetch user for edit mode
  // ---------------------------------------------------------------------------
  const { data: editUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user-gestao", userId],
    queryFn: async (): Promise<UserWithPermissions> => {
      const data = await keycloakApi.fetchUserById(userId!);
      return {
        user_id: data.id.toString(),
        auth_id: data.keycloak_id || "",
        user_name: data.fullname,
        email: data.email,
        permission_role: data.role,
        is_internal: false,
        phone_number: data.phone,
        created_at: data.created_at,
        generator_links: data.generator_links,
        generators: null,
        establishments: [],
      };
    },
    enabled: isEditMode && !!userId,
  });

  // ---------------------------------------------------------------------------
  // Perfis de acesso (Keycloak Groups) — GR-637
  // ---------------------------------------------------------------------------
  // Query separada porque `GET /users/{pk}` não devolve os grupos: eles vivem
  // no Keycloak, não na tabela de usuários.
  const {
    data: userProfilesData,
    isLoading: isLoadingProfiles,
    isError: isProfilesError,
    error: profilesError,
  } = useQuery({
    queryKey: ["permission-profiles", userId],
    queryFn: () => keycloakApi.fetchUserProfiles(userId!),
    enabled: isEditMode && !!userId,
    // 404 é "este usuário não tem conta no Keycloak" e 401/403 é permissão —
    // nenhum dos três melhora com retry, e o backend do GR-637 distingue os
    // três justamente para a tela não ficar insistindo.
    retry: (failureCount, error) => {
      const status = (error as { httpStatus?: number })?.httpStatus;
      if (status === 404 || status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

  // Na criação, sugerir o acesso base; na edição, o estado real do servidor.
  // Criação já nasce com o acesso base; edição espera a hidratação do servidor.
  const [selectedProfiles, setSelectedProfiles] = React.useState<string[]>(
    isEditMode ? [] : withMandatoryProfiles([]),
  );
  const [pendingProfiles, setPendingProfiles] =
    React.useState<PendingProfiles | null>(null);
  // Hidrata uma vez só. Sem a trava, um refetch (o QueryClient global roda com
  // staleTime 0) sobrescreveria o que o admin acabou de marcar.
  const hasHydratedProfiles = React.useRef(false);

  // ---------------------------------------------------------------------------
  // Verificação de e-mail + reativação de usuário excluído (MC-91)
  // ---------------------------------------------------------------------------
  const [emailInput, setEmailInput] = React.useState("");
  const debouncedEmail = useDebouncedValue(emailInput, 400);
  const [isReactivating, setIsReactivating] = React.useState(false);
  const [dismissedEmail, setDismissedEmail] = React.useState<string | null>(null);

  const { data: emailCheck } = useQuery({
    queryKey: ["user-email-lookup", debouncedEmail],
    queryFn: () => keycloakApi.lookupUserByEmail(debouncedEmail),
    // Só na criação, e só pra algo que já parece um e-mail — evita consulta a
    // cada tecla/blur de um campo ainda incompleto.
    enabled: !isEditMode && /^\S+@\S+\.\S+$/.test(debouncedEmail),
  });

  // E-mail mudou de novo: a decisão de reativar (ou o "Cancelar" anterior) era
  // sobre o e-mail velho, não serve mais pro novo.
  React.useEffect(() => {
    setIsReactivating(false);
    setDismissedEmail(null);
  }, [debouncedEmail]);

  const showEmailBanner =
    !isEditMode &&
    !isReactivating &&
    emailCheck &&
    emailCheck.status !== "available" &&
    dismissedEmail !== debouncedEmail;

  const emailBannerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!showEmailBanner) return;
    emailBannerRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    emailBannerRef.current?.focus();
  }, [showEmailBanner]);

  useEffect(() => {
    if (!isEditMode || hasHydratedProfiles.current || !userProfilesData || !editUser) return;

    // RECONCILIAÇÃO DO ACESSO LEGADO (GR-637).
    //
    // Os grupos do catálogo são novos e a migração dos usuários existentes é
    // um script à parte, que ainda não rodou. Até lá o Keycloak não sabe o
    // que a pessoa realmente pode fazer: o `GET /profiles` volta vazio para
    // todo mundo que veio de antes. E `role` agora é DERIVADO do que está
    // marcado aqui (ver UserForm.handleSubmit) — então abrir a edição de um
    // usuário legado e salvar qualquer campo gravaria o vazio por cima do
    // acesso real. Perda de acesso silenciosa, sem ninguém desmarcar nada.
    //
    // A seleção nasce refletindo o acesso VERDADEIRO, montado do que o
    // Portal sabe hoje:
    //
    //   - `default`: acesso base, OBRIGATÓRIO. Entra sempre, inclusive para
    //     quem já foi migrado e está sem ele — nesse caso a hidratação
    //     funciona como conserto: o diff nasce com `add: ["default"]` e a
    //     tela mostra o badge "novo" antes de gravar.
    //   - `admin`: quando `editUser.permission_role` (que vem da coluna
    //     `generator_links.role`, não do Keycloak) diz admin.
    //
    // Desmarcar passa a ser ato explícito do admin, e salvar aplica o PUT
    // que corrige a membership — a tela vai migrando o legado aos poucos,
    // sem depender do script.
    //
    // LIMITE: só dá para reconstruir o que o Portal enxerga. Acesso avulso
    // concedido por client role direta (ex.: `view-collect-evidence` do caso
    // Daki, GR-563) não aparece em `GET /profiles` nem em `role` — esse é o
    // recorte que só o script de migração alcança.
    const reconciled = withMandatoryProfiles([...userProfilesData.profiles]);
    if (
      editUser.permission_role === "admin" &&
      !reconciled.includes(ADMIN_PROFILE_KEY)
    ) {
      reconciled.push(ADMIN_PROFILE_KEY);
    }

    setSelectedProfiles(reconciled);
    hasHydratedProfiles.current = true;
  }, [isEditMode, userProfilesData, editUser]);

  /** Estado no servidor — base do diff. Nunca o estado inicial do formulário. */
  const profilesBaseline = userProfilesData?.profiles ?? [];

  // Achado em review: o formulário é longo (o botão Salvar fica no rodapé) e
  // os avisos de estado renderizam no topo da página, sem `navigate` e sem
  // nada mudar visualmente perto do clique — quem está com a tela rolada até
  // o fim não vê o aviso aparecer. `role="alert"` já cobre leitor de tela;
  // isto cobre quem enxerga.
  const bannerRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pendingProfiles && !isProfilesError) return;
    bannerRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    bannerRef.current?.focus();
  }, [pendingProfiles, isProfilesError]);

  // ---------------------------------------------------------------------------
  // Create mutation
  // ---------------------------------------------------------------------------
  const createUserMutation = useMutation({
    mutationFn: async (userData: SubmitData) => {
      if (!userData.generator_links?.length) {
        throw new Error("Selecione ao menos um gerador antes de criar o usuário.");
      }
      const fullname =
        userData.firstName && userData.lastName
          ? `${userData.firstName.trim()} ${userData.lastName.trim()}`
          : userData.user_name || "";

      const created = await keycloakApi.createUser({
        email: userData.email!,
        fullname,
        phone: userData.phone_number || undefined,
        generator_links: userData.generator_links,
        password: userData.temp_password!,
      });

      // Segunda escrita, sem transação: `POST /users` não aceita perfis, e o
      // `pk` só existe depois dele. Uma falha aqui NÃO desfaz a criação — o
      // usuário existe. Devolver isso como resultado (e não como exceção)
      // evita o toast mentiroso "erro ao criar usuário".
      // `?? []` em vez de sair antes: `diffProfiles` normaliza o obrigatório, de
      // modo que um `profiles` ausente vira `add: ["default"]` — nunca usuário
      // sem acesso base.
      const desired = userData.profiles ?? [];
      const diff = diffProfiles([], desired);
      if (diff.add.length === 0) {
        return { status: "ok" as const, user: created };
      }

      try {
        const result = await keycloakApi.updateUserProfiles(created.id, diff);
        if (result.failed?.length) {
          return {
            status: "profiles-failed" as const,
            user: created,
            desired,
            baseline: result.added ?? [],
            reason: "Parte dos perfis não pôde ser aplicada.",
            failed: result.failed,
          };
        }
        return { status: "ok" as const, user: created };
      } catch (error) {
        return {
          status: "profiles-failed" as const,
          user: created,
          desired,
          baseline: [],
          reason:
            (error as Error)?.message ||
            "Não foi possível falar com o Keycloak para aplicar os perfis.",
        };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["users-with-keycloak"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-permissions"] });

      if (result.status === "profiles-failed") {
        trackError({ errorType: "api_error", errorCode: "perfis_nao_aplicados" });
        setPendingProfiles({
          userId: result.user.id,
          email: result.user.email,
          desired: result.desired,
          baseline: result.baseline,
          reason: result.reason,
          failed: result.failed,
          fromCreation: true,
          fromReactivation: isReactivating,
        });
        return;
      }

      trackButtonClick({ buttonLabel: "usuarios_criar_sucesso" });
      toast({
        title: isReactivating ? "Usuário reativado com sucesso" : "Usuário criado com sucesso",
        description: isReactivating
          ? "O usuário voltou a ter acesso ao portal."
          : "O novo usuário foi adicionado ao sistema.",
      });
      navigate("/usuarios");
    },
    onError: (error: Error & { code?: string }) => {
      // RN-05: código de lista fechada, nunca `error.message` (pode conter o e-mail).
      trackError({ errorType: "api_error", errorCode: toApiErrorCode(error) });

      // MC-91: o backend manda `code` estruturado com `message` já em
      // linguagem comum (StructuredAPIException) — só escolhemos o título,
      // sem inventar texto por substring.
      const titleByCode: Record<string, string> = {
        user_email_already_exists: "⚠️ Usuário já cadastrado",
        user_account_conflict: "⚠️ Conflito na conta de acesso",
      };
      const title = (error.code && titleByCode[error.code]) || "Erro ao criar usuário";
      const description = error.message || "Ocorreu um erro inesperado.";

      toast({ title, description, variant: "destructive", duration: 6000 });
    },
  });

  // ---------------------------------------------------------------------------
  // Update mutation (lógica espelhada do EditUserModal)
  // ---------------------------------------------------------------------------
  const updateUserMutation = useMutation({
    mutationFn: async (userData: SubmitData) => {
      if (!editUser) throw new Error("Usuário não encontrado");

      const userIdNumber = parseInt(editUser.user_id, 10);
      if (isNaN(userIdNumber)) throw new Error("ID do usuário inválido");

      // Alterar senha (apenas quando editando o próprio perfil)
      if (userData.new_password && isEditingSelf) {
        if (!isPasswordRecovery && !userData.current_password) {
          throw new Error("Por favor, forneça sua senha atual para confirmar a alteração.");
        }
        await keycloakApi.updateUser(userIdNumber, { password: userData.new_password });
      }

      const newFullname = `${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim();

      const updatePayload: Parameters<typeof keycloakApi.updateUser>[1] = {};

      if (newFullname && newFullname !== editUser.user_name) {
        updatePayload.fullname = newFullname;
      }
      if (userData.phone_number !== editUser.phone_number) {
        updatePayload.phone = userData.phone_number || null;
      }
      if (!isEditingSelf && !userData.generator_links && userData.permission_role !== editUser.permission_role) {
        updatePayload.role = userData.permission_role;
      }
      if (userData.generator_links) {
        updatePayload.generator_links = userData.generator_links;
      } else {
        const generatorId = editUser.generators?.id_new_engine_generators ?? null;
        if (generatorId) updatePayload.generator_id = generatorId;
      }

      // Achado em review: o PATCH roda ANTES do PUT de perfis, e já carrega o
      // `role` derivado (dentro de `updatePayload.role` OU de cada
      // `generator_links[].role`). Se o PUT falhar depois, o nível de acesso
      // já mudou de verdade — capturado aqui para o aviso não dizer só
      // "perfis não aplicados" quando o que já aconteceu é mais que isso.
      const roleChangedOnServer =
        Object.keys(updatePayload).length > 0 &&
        userData.permission_role !== editUser.permission_role;

      if (Object.keys(updatePayload).length > 0) {
        await keycloakApi.updateUser(userIdNumber, updatePayload);

        // Sincronizar com Supabase
        if (keycloakToken) {
          try {
            const supabasePayload: Record<string, any> = { email: userData.email };
            if (newFullname && newFullname !== editUser.user_name) supabasePayload.user_name = newFullname;
            if (userData.phone_number !== editUser.phone_number) supabasePayload.phone_number = userData.phone_number || null;
            if (!isEditingSelf && userData.permission_role !== editUser.permission_role) supabasePayload.permission_role = userData.permission_role;

            await supabase.functions.invoke("update-user", {
              body: supabasePayload,
              headers: { "x-keycloak-token": keycloakToken },
            });
          } catch (err) {
            console.warn("Erro ao sincronizar com Supabase:", err);
          }
        }
      }

      // FORA do `if` acima de propósito: uma edição que só mexeu nos perfis
      // deixa `updatePayload` vazio, e aninhar aqui dentro faria o Salvar não
      // fazer nada. O diff sai do estado do servidor (`profilesBaseline`), não
      // do estado inicial do formulário — senão uma alteração feita em outra
      // aba entre a abertura e o Salvar produz add/remove errados.
      if (userData.profiles) {
        const diff = diffProfiles(profilesBaseline, userData.profiles);
        if (diff.add.length > 0 || diff.remove.length > 0) {
          try {
            const result = await keycloakApi.updateUserProfiles(userIdNumber, diff);
            if (result.failed?.length) {
              return {
                status: "profiles-failed" as const,
                userData,
                failed: result.failed,
                reason: "Parte dos perfis não pôde ser aplicada.",
                roleAlreadyChanged: roleChangedOnServer,
              };
            }
          } catch (error) {
            return {
              status: "profiles-failed" as const,
              userData,
              reason:
                (error as Error)?.message ||
                "Não foi possível falar com o Keycloak para aplicar os perfis.",
              roleAlreadyChanged: roleChangedOnServer,
            };
          }
        }
      }

      return { status: "ok" as const, userData };
    },
    onSuccess: async (result) => {
      const variables = result.userData;

      queryClient.invalidateQueries({ queryKey: ["users-with-keycloak"] });
      queryClient.invalidateQueries({ queryKey: ["users-with-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["user-gestao", userId] });
      queryClient.invalidateQueries({ queryKey: ["permission-profiles", userId] });

      if (result.status === "profiles-failed" && editUser) {
        setPendingProfiles({
          userId: parseInt(editUser.user_id, 10),
          email: editUser.email,
          desired: variables.profiles ?? [],
          baseline: profilesBaseline,
          reason: result.reason,
          failed: result.failed,
          fromCreation: false,
          roleAlreadyChanged: result.roleAlreadyChanged,
        });
        return;
      }

      const passwordUpdated = variables.new_password && isEditingSelf;
      toast({
        title: "Usuário atualizado com sucesso",
        description: passwordUpdated
          ? "As alterações foram salvas e sua senha foi atualizada."
          : "As alterações foram salvas.",
      });

      if (editUser) {
        const targetUserEmailHash = await hashEmail(editUser.email);
        const generatorLinks = editUser.generator_links ?? [];
        trackButtonClick({
          buttonLabel: "usuarios_editar_usuario",
          target_user_id: editUser.user_id,
          target_user_role: editUser.permission_role ?? "",
          target_user_email_hash: targetUserEmailHash,
          generator_ids_affected: generatorLinks.map((g) => g.generator_id).join(","),
          generator_count_affected: generatorLinks.length,
        });
      }

      navigate("/usuarios");
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar usuário",
        description: error.message || "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Reaplicar só os perfis — GR-637
  // ---------------------------------------------------------------------------
  // O usuário já está gravado; o que faltou foi o diff de perfis. Repetir o
  // submit inteiro bateria em e-mail duplicado na criação, então o retry
  // ataca só a parte que falhou.
  const retryProfilesMutation = useMutation({
    mutationFn: async () => {
      if (!pendingProfiles) throw new Error("Nada para reaplicar");
      const diff = diffProfiles(pendingProfiles.baseline, pendingProfiles.desired);
      return keycloakApi.updateUserProfiles(pendingProfiles.userId, diff);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["permission-profiles", userId] });
      queryClient.invalidateQueries({ queryKey: ["users-with-keycloak"] });

      if (result.failed?.length) {
        setPendingProfiles((current) =>
          current
            ? {
                ...current,
                baseline: [...current.baseline, ...(result.added ?? [])],
                reason: "Parte dos perfis ainda não pôde ser aplicada.",
                failed: result.failed,
              }
            : current,
        );
        return;
      }

      toast({
        title: "Perfis aplicados",
        description: "Os perfis de acesso foram salvos.",
      });
      setPendingProfiles(null);
      navigate("/usuarios");
    },
    onError: (error: Error) => {
      setPendingProfiles((current) =>
        current
          ? { ...current, reason: error.message || "A tentativa falhou de novo." }
          : current,
      );
    },
  });

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  const handleSubmit = (data: SubmitData) => {
    if (isEditMode) {
      updateUserMutation.mutate(data);
    } else {
      createUserMutation.mutate(data);
    }
  };

  const isLoading = isEditMode ? updateUserMutation.isPending : createUserMutation.isPending;

  // ---------------------------------------------------------------------------
  // Quem pode abrir esta tela (GR-637)
  // ---------------------------------------------------------------------------
  // Esta é a tela que CONCEDE permissão, e até agora só o item de menu a
  // escondia — a URL entregava o formulário de criação/edição a qualquer
  // usuário autenticado. `/usuarios` já se protegia assim (UsersKeycloak.tsx);
  // aqui faltava.
  //
  // O critério é `canManageUsers`, o mesmo da listagem, e NÃO uma checagem de
  // `manage-users` via PermissionRoute: aquele guard deixa o não migrado passar
  // (GR-561, regra 16), o que aqui abriria a tela em vez de fechá-la.
  // `canManageUsers` cobre os dois regimes — grupo `admin` para migrado, papel
  // legado para quem ainda não foi.
  if (!isAuthLoading && !canManageUsers) {
    return <AccessDenied area="à gestão de usuários" />;
  }

  // ---------------------------------------------------------------------------
  // Loading state (edit mode fetching user)
  // ---------------------------------------------------------------------------
  if (isEditMode && isLoadingUser) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6 w-full flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const title = isEditMode ? "Editar Usuário" : "Adicionar Novo Usuário";
  const subtitle = isEditMode
    ? "Altere as informações e permissões do usuário."
    : "Crie um novo usuário e defina suas permissões de acesso.";

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-2xl mx-auto space-y-4 md:space-y-6">
        {/* Header */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (!isEditMode) {
                  trackButtonClick({ buttonLabel: "usuarios_criar_formulario_incompleto" });
                }
                navigate("/usuarios");
              }}
              className="p-1 rounded-md hover:bg-accent transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-[#10261F]" />
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-[#10261F]">
              {title}
            </h1>
          </div>
          <p className="text-gray-600 mt-2 text-sm md:text-base">{subtitle}</p>
        </div>

        {/* Form */}
        {pendingProfiles && (
          <div
            ref={bannerRef}
            role="alert"
            tabIndex={-1}
            className="rounded-lg border border-warning-300 bg-warning-100 p-4 outline-none"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-800" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-warning-800">
                  {pendingProfiles.fromReactivation
                    ? "Usuário reativado, perfis não aplicados"
                    : pendingProfiles.fromCreation
                      ? "Usuário criado, perfis não aplicados"
                      : "Dados salvos, perfis não aplicados"}
                </p>
                <p className="mt-1 text-sm text-warning-800">
                  {pendingProfiles.fromReactivation
                    ? `O usuário ${pendingProfiles.email} foi reativado, mas os perfis de acesso não foram aplicados.`
                    : pendingProfiles.fromCreation
                      ? `O usuário ${pendingProfiles.email} foi criado, mas os perfis de acesso não foram aplicados.`
                      : `As alterações de ${pendingProfiles.email} foram salvas, mas os perfis de acesso não foram aplicados.`}{" "}
                  {pendingProfiles.reason}
                </p>
                {pendingProfiles.roleAlreadyChanged && (
                  <p className="mt-1 text-sm font-medium text-warning-800">
                    O perfil de acesso (Admin/Operacional) já foi alterado —
                    só os perfis de permissão ficaram pendentes. Tentar de
                    novo reaplica só os perfis, não desfaz essa parte.
                  </p>
                )}

                {pendingProfiles.failed?.length ? (
                  <ul className="mt-2 space-y-1 text-xs text-warning-800">
                    {pendingProfiles.failed.map((failure) => (
                      <li key={`${failure.action}-${failure.profile}`}>
                        {failure.action === "add" ? "Adicionar" : "Remover"}{" "}
                        <strong>{getPermissionProfileLabel(failure.profile)}</strong> —{" "}
                        {failure.error}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={() => retryProfilesMutation.mutate()}
                    disabled={retryProfilesMutation.isPending}
                  >
                    {retryProfilesMutation.isPending
                      ? "Aplicando..."
                      : "Tentar aplicar de novo"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setPendingProfiles(null);
                      navigate("/usuarios");
                    }}
                  >
                    Ir para a lista
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isEditMode && isProfilesError && (
          <div
            ref={bannerRef}
            role="alert"
            tabIndex={-1}
            className="rounded-lg border border-destructive bg-card p-4 text-sm text-destructive outline-none"
          >
            Não foi possível carregar os perfis de acesso deste usuário
            {/* Só afirma "sem conta no Keycloak" quando o 404 veio da própria
                API em JSON. Um 404 de corpo HTML é a rota não existir (backend
                numa versão anterior, proxy errado) — culpar o cadastro do
                usuário aí manda depurar no lugar errado. */}
            {(profilesError as { httpStatus?: number; fromApi?: boolean })
              ?.httpStatus === 404 &&
            (profilesError as { fromApi?: boolean })?.fromApi
              ? " porque ele não tem conta correspondente no Keycloak."
              : "."}{" "}
            Os demais dados podem ser editados normalmente; os perfis ficam
            indisponíveis até a leitura voltar.
          </div>
        )}

        {/* E-mail já em uso (ativo) ou pertencente a usuário excluído/incompleto — MC-91 */}
        {showEmailBanner && emailCheck && (
          <div
            ref={emailBannerRef}
            role="alert"
            tabIndex={-1}
            className="rounded-lg border border-warning-300 bg-warning-100 p-4 outline-none"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning-800" />
              <div className="min-w-0 flex-1">
                {emailCheck.status === "active" ? (
                  <>
                    <p className="text-sm font-semibold text-warning-800">
                      Já existe um usuário ativo com este e-mail
                    </p>
                    <p className="mt-1 text-sm text-warning-800">
                      Pra alterar os acessos dele, procure{" "}
                      <strong>{debouncedEmail}</strong> na lista de usuários em
                      vez de criar outro cadastro.
                    </p>
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() =>
                          navigate(`/usuarios?email=${encodeURIComponent(debouncedEmail)}`)
                        }
                      >
                        Ver na lista de usuários
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-warning-800">
                      {emailCheck.status === "deleted"
                        ? "Este e-mail pertenceu a um usuário excluído"
                        : "Este e-mail já tem um cadastro incompleto"}
                    </p>
                    <p className="mt-1 text-sm text-warning-800">
                      {emailCheck.user ? (
                        <>
                          <strong>{emailCheck.user.fullname}</strong>
                          {emailCheck.status === "deleted" && (
                            <> — excluído em{" "}
                              {new Date(emailCheck.user.updated_at).toLocaleDateString("pt-BR")}
                            </>
                          )}
                          . Reativar recupera os dados e os acessos que esse
                          usuário tinha antes, prontos pra ajustar antes de
                          confirmar.
                        </>
                      ) : (
                        "Existe uma conta de acesso com este e-mail, sem nenhum "
                          + "cadastro vinculado. Reativar aproveita essa conta — "
                          + "é só escolher ao menos um gerador e confirmar."
                      )}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" onClick={() => setIsReactivating(true)}>
                        Reativar usuário
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setDismissedEmail(debouncedEmail)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <UserForm
          user={isEditMode ? editUser : undefined}
          generatorOptions={generatorOptions}
          establishmentOptions={establishmentOptions}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          isEditingSelf={isEditingSelf}
          openPasswordSection={isEditMode && openPasswordSection && !!isEditingSelf}
          isPasswordRecovery={isEditMode && isPasswordRecovery && !!isEditingSelf}
          // Só liga quando dá para escrever: em edição, sem o GET que serve de
          // base do diff, um `remove` sairia calculado contra lista vazia.
          showPermissionProfiles={!isEditMode || !isProfilesError}
          profiles={selectedProfiles}
          onProfilesChange={setSelectedProfiles}
          isLoadingProfiles={isEditMode && isLoadingProfiles}
          // Só em edição: na criação não há estado anterior, e marcar tudo de
          // "novo" seria ruído sem informação.
          profilesBaseline={isEditMode ? userProfilesData?.profiles : undefined}
          onEmailBlur={!isEditMode ? setEmailInput : undefined}
          isReactivating={!isEditMode && isReactivating}
          prefillData={
            !isEditMode && isReactivating && emailCheck?.user
              ? {
                  fullname: emailCheck.user.fullname,
                  phone: emailCheck.user.phone,
                  role: emailCheck.user.role,
                  generator_links: emailCheck.user.generator_links,
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default GestaoUsuario;
